import axios from "axios";
import { MSGraphAuth } from "./GraphService";
import { ProjectParams } from "../config/GraphConfig";
import { urlFormat } from "../library/utils";
import { ROUTES } from "library/constants";
import {
  OffersAzureResponse,
  Setting,
  SettingsResponse,
  TaxonomyUADResponse,
} from "library/types/types_old";
import { SearchOffersArgs } from "state/queries/useGetOffers";
import { createAzureFilterParam } from "library/utils/azureSearchFilterParam";

let authHeaders = {};
export type InvalidateOfferParams = {
  offerId: any;
  stornoType?: string; // Add stornoType parameter for cancellations
};
const url = urlFormat(process.env.REACT_APP_BASE_URL || "");
const uadUrl = urlFormat(process.env.REACT_APP_BFF_HOST_UAD || "");

const axiosInstance = axios.create({
  baseURL: url,
});

async function execute(func: any, url: string, config: {}) {
  try {
    const response = await func(url, config);
    return {
      status: response?.status,
      statusText: response.statusText,
      data: response.data,
    };
  } catch (error: any) {
    if (error.response?.status === 403) {
      window.location.href = ROUTES.UNAUTHORIZED_PAGE;
    }
    // if (error.response) {
    //   const err = {
    //     status: error?.response?.status,
    //     statusText: error?.response?.statusText,
    //     error:
    //       error?.response?.data?.error || error?.response?.data?.data?.error,
    //     message:
    //       error?.response?.data?.message ||
    //       error?.response?.data?.data?.message ||
    //       "",
    //   };

    //   throw err;
    // }

    throw error;
  }
}

const getAppSettings = async (appID: number) => {
  return await axiosInstance.get<SettingsResponse>(
    `${uadUrl}/appsettings/${appID}`
  );
};

const getUADTaxonomy = async () => {
  return await axiosInstance.get<TaxonomyUADResponse>(`${uadUrl}/taxonomy`);
};

const addAppSetting = async (setting: Partial<Setting>) => {
  return await axiosInstance.post<Setting[]>(`${uadUrl}/addappsettings`, [
    setting,
  ]);
};

const updateAppSetting = async (setting: Setting) => {
  return await axiosInstance.patch<Setting[]>(`${uadUrl}/updateappsettings`, [
    setting,
  ]);
};

const invalidateOffer = async ({
  offerId,
  stornoType,
}: InvalidateOfferParams) => {
  let reqUrl: string = `${url}/offers/invalidate/${offerId}`;
  // If stornoType is provided, add it as a query parameter
  if (stornoType) {
    reqUrl += `?stornoType=${stornoType}`;
  }
  const res = await axiosInstance.patch<any>(reqUrl);
  return res;
};

const deleteAppSetting = async (
  userAppSettingId: Setting["userAppSettingId"]
) => {
  return await axiosInstance.delete<Setting[]>(`${uadUrl}/deleteappsettings`, {
    data: [userAppSettingId],
  });
};

const searchOffers = async ({
  skip,
  top,
  search,
  sort,
  cancelToken,
  columns,
}: SearchOffersArgs) => {
  let searchUrl = `${url}/offers`;
  searchUrl += `?searchCriteria=${search.replace(
    /&/g,
    encodeURIComponent("&")
  )}*`;
  if (sort.key !== "" && sort.dir !== "") {
    searchUrl += `&$orderby=${sort.key} ${sort.dir}`;
  }
  searchUrl += `&form=json`;
  searchUrl += `&$count=true`;
  searchUrl += `&$top=${top}&$skip=${skip}`;

  let filterStr = createAzureFilterParam(columns as any);

  if (filterStr.length > 0) {
    searchUrl += `&$filter=${filterStr}`;
  }

  const res = await axiosInstance.get<OffersAzureResponse>(searchUrl, {
    cancelToken,
  });
  return res;
};

const getVehicleInfoByVin = async (vin: string) => {
  if (!vin || vin.trim().length === 0) {
    throw new Error("VIN parameter is required");
  }

  const res = await axiosInstance.get(`${url}/vehicles/info/${vin.trim()}`);
  return res;
};

const getClientInfoByIco = async (ico: string) => {
  if (!ico || ico.trim().length === 0) {
    throw new Error("ICO parameter is required");
  }

  const res = await axiosInstance.get(`${url}/clients/info/${ico.trim()}`);
  return res;
};

export const calculatePremiums = async (calculationRequest: any) => {
  const res = await axiosInstance.post(
    `${url}/calculations`,
    calculationRequest
  );
  return res;
};

const api = {
  setAuthHeaders: (headers: {}) => {
    authHeaders = {
      ...authHeaders,
      ...headers,
    };
  },
  get: async (url: string, config = {}) =>
    execute(axiosInstance.get, url, config),
  post: async (url: string, config = {}) =>
    execute(axiosInstance.post, url, config),
  put: async (url: string, config = {}) =>
    execute(axiosInstance.put, url, config),
  patch: async (url: string, config = {}) =>
    execute(axiosInstance.patch, url, config),
  delete: async (url: string, config = {}) =>
    execute(axiosInstance.delete, url, config),
  getAppSettings: getAppSettings,
  getUADTaxonomy: getUADTaxonomy,
  addAppSetting: addAppSetting,
  deleteAppSetting: deleteAppSetting,
  updateAppSetting: updateAppSetting,
  invalidateOffer: invalidateOffer,
  searchOffers: searchOffers,
  getVehicleInfoByVin: getVehicleInfoByVin,
  getClientInfoByIco: getClientInfoByIco,
  calculatePremiums: calculatePremiums,
};

export default api;

axiosInstance.interceptors.request.use(async (config) => {
  let authResponse = await MSGraphAuth.getAccessToken(ProjectParams);
  if (!authResponse) return config;
  if (config.url?.indexOf("photo/$value") !== -1) {
    return config;
  }
  config!.headers!.Authorization = `Bearer ${authResponse.accessToken}`;
  return config;
});
